import {
  createShapeId,
  Editor,
  getIndexAbove,
  IndexKey,
  TLShape,
  TLShapeId,
} from "tldraw";

import { isPageRegionChild } from "@/components/canvas-editor/bindings/layout";
import { CanvasEditorProps } from "@/components/canvas-editor/CanvasEditor";
import {
  buildLayerShapes,
  buildSortSettings,
  deleteLayerShapes,
} from "@/components/canvas-editor/layers";
import { isAttributeCompareShape } from "@/components/canvas-editor/shapes/attribute-compare";
import { isContentModerationShape } from "@/components/canvas-editor/shapes/content-moderation";
import { getCustomNodeRootShape } from "@/components/canvas-editor/shapes/custom-node-definition";
import { isEmbedShape } from "@/components/canvas-editor/shapes/embed";
import { isEmotionAnalysisShape } from "@/components/canvas-editor/shapes/emotion-analysis";
import { isEntityListShape } from "@/components/canvas-editor/shapes/entity-list";
import { getFeedShape } from "@/components/canvas-editor/shapes/feed";
import { isFinancialSentimentAnalysisShape } from "@/components/canvas-editor/shapes/financial-sentiment-analysis";
import { isImageCategorizationShape } from "@/components/canvas-editor/shapes/image-categorization";
import { isImageModerationShape } from "@/components/canvas-editor/shapes/image-moderation";
import { isLanguageAnalysisShape } from "@/components/canvas-editor/shapes/language-analysis";
import { isListMemberShape } from "@/components/canvas-editor/shapes/list-member";
import { isMagicAudienceShape } from "@/components/canvas-editor/shapes/magic-audience";
import { isModelProbabilityShape } from "@/components/canvas-editor/shapes/model-probability";
import { isMultiRegexShape } from "@/components/canvas-editor/shapes/multi-regex";
import { isOpenAIShape } from "@/components/canvas-editor/shapes/openai";
import { isParamCompareShape } from "@/components/canvas-editor/shapes/param-compare";
import { isPostTypeShape } from "@/components/canvas-editor/shapes/post-type";
import { isRatioCompareShape } from "@/components/canvas-editor/shapes/ratio-compare";
import { isSentimentAnalysisShape } from "@/components/canvas-editor/shapes/sentiment-analysis";
import {
  isAttributeCompareFilter,
  dateBetweenType,
  isDateBetweenFilter,
  isHourOfDayBetweenFilter,
  isRatioCompareFilter,
  isTextQuestionFilter,
  hourOfDayBetweenType,
  makeDateBetweenFilter,
  makeHourOfDayBetweenFilter,
  makeRatioCompareFilter,
  makeTextQuestionFilter,
  ratioCompareType,
  textQuestionProps,
  textQuestionType,
  readTimeWindows,
  isEmbedFilter,
  isContentModerationFilter,
  isListMemberFilter,
  isLogicShape,
  isRegexFilter,
  isRegexNegationFilter,
  isRegexAnyFilter,
  isRegexNoneFilter,
  isSocialGraphFilter,
  isSocialListFilter,
  isStarterPackFilter,
  isMagicAudienceFilter,
  isTextSimilarityFilter,
  isModelProbabilityFilter,
  makeAttributeCompareFilter,
  makeEmbedFilter,
  makeContentModerationFilter,
  makeListMemberFilter,
  makeRegexFilter,
  makeSocialGraphFilter,
  makeSocialListFilter,
  makeStartPackFilter,
  makeMagicAudienceFilter,
  makeTextSimilarityFilter,
  makeModelProbabilityFilter,
  embedType,
  socialGraphType,
  logicType,
  simpleRegexType,
  multiRegexType,
  textSimilarityType,
  attributeCompareType,
  starterPackType,
  listMemberType,
  socialListType,
  magicAudienceType,
  modelProbabilityType,
  contentModerationType,
  LayoutBinding,
  isEntityListFilter,
  entityListType,
  makeEntityListFilter,
  isLanguageAnalysisFilter,
  languageAnalysisType,
  makeLanguageAnalysisFilter,
  sentimentAnalysisType,
  makeSentimentAnalysisFilter,
  isSentimentAnalysisFilter,
  financialSentimentAnalysisType,
  makeFinancialSentimentAnalysisFilter,
  isFinancialSentimentAnalysisFilter,
  toxicityAnalysisType,
  makeToxicityAnalysisFilter,
  isToxicityAnalysisFilter,
  emotionAnalysisType,
  makeEmotionAnalysisFilter,
  isEmotionAnalysisFilter,
  spamAnalysisType,
  makeSpamAnalysisFilter,
  isSpamAnalysisFilter,
  topicAnalysisType,
  makeTopicAnalysisFilter,
  isTopicAnalysisFilter,
  CustomShapes,
  textCategorizationType,
  makeTextCategorizationFilter,
  isTextCategorizationFilter,
  imageModerationType,
  makeImageModerationFilter,
  isImageModerationFilter,
  imageCategorizationType,
  makeImageCategorizationFilter,
  isImageCategorizationFilter,
  isPostTypeFilter,
  makePostTypeFilter,
  postTypeType,
  isCustomNodeShape,
  metadataKeys,
  CustomNodeShape,
  customNodeType,
  makeParamCompareFilter,
  isParamCompareFilter,
  paramCompareType,
  isOpenAIFilter,
  makeOpenAIFilter,
  openAIFilterArgs,
  openaiType,
  isEachShape,
  isEachFilter,
  eachType,
  feedType,
  customNodeRootType,
  isCustomNodeRootShape,
  CustomNodeRootShape,
  FeedShape,
  isFeedShape,
} from "@/components/canvas-editor/shapes/shapes";
import { isSimpleRegexShape } from "@/components/canvas-editor/shapes/simple-regex";
import { isSocialGraphShape } from "@/components/canvas-editor/shapes/social-graph";
import { isSocialListShape } from "@/components/canvas-editor/shapes/social-list";
import { isSpamAnalysisShape } from "@/components/canvas-editor/shapes/spam-analysis";
import { isStarterPackShape } from "@/components/canvas-editor/shapes/starter-pack";
import { isTextCategorizationShape } from "@/components/canvas-editor/shapes/text-categorization";
import { isTextQuestionShape } from "@/components/canvas-editor/shapes/text-question";
import { isTextSimilarityShape } from "@/components/canvas-editor/shapes/text-similarity";
import {
  isDateBetweenShape,
  isHourOfDayBetweenShape,
} from "@/components/canvas-editor/shapes/time-window";
import { isTopicAnalysisShape } from "@/components/canvas-editor/shapes/topic-analysis";
import { isToxicityAnalysisShape } from "@/components/canvas-editor/shapes/toxicity-analysis";
import { feedId } from "@/components/canvas-editor/utils";
import {
  FeedListParameter,
  FeedParameter,
  FeedVersion,
  FilterTypes,
  Manifest,
  NodeMetadata,
  SaveFilter,
  SaveManifest,
  SortingParams,
} from "@/lib/types";

// A container's children are written into the manifest in the order they sit on
// the canvas, top to bottom. That makes this comparator part of the manifest's
// contract, not a display detail: the audit tree Debug gets back is in this
// order, so anything pairing canvas nodes with audit nodes has to sort the same
// way (see `annotate-feed.ts`). The layout binding index usually agrees and is
// NOT a safe substitute -- on 3 of 4,704 live feeds the two had diverged.
export function compareShapesByCanvasPosition(editor: Editor) {
  return (a: TLShape, b: TLShape) => {
    const aY = editor.getPointInShapeSpace(a, { x: 0, y: 0 }).y;
    const bY = editor.getPointInShapeSpace(b, { x: 0, y: 0 }).y;

    return bY - aY;
  };
}

// Exported for `annotate-feed.ts`, which needs a leaf shape's operator name to
// pair canvas nodes with audit nodes. Leaf shapes cost nothing here; do not call
// this per-node on a container, it rebuilds the whole subtree.
export function buildTree(editor: Editor, shape: TLShape): Manifest["filter"] {
  if (isParamCompareShape(shape)) {
    return makeParamCompareFilter(shape.props);
  }

  if (isSimpleRegexShape(shape)) {
    return makeRegexFilter(shape.props);
  }

  if (isMultiRegexShape(shape)) {
    return makeRegexFilter(shape.props);
  }

  if (isAttributeCompareShape(shape)) {
    return makeAttributeCompareFilter(shape.props);
  }

  if (isHourOfDayBetweenShape(shape)) {
    return makeHourOfDayBetweenFilter(shape.props);
  }

  if (isDateBetweenShape(shape)) {
    return makeDateBetweenFilter(shape.props);
  }

  if (isRatioCompareShape(shape)) {
    return makeRatioCompareFilter(shape.props);
  }

  if (isTextQuestionShape(shape)) {
    return makeTextQuestionFilter(shape.props);
  }

  if (isEmbedShape(shape)) {
    return makeEmbedFilter(shape.props);
  }

  if (isTextSimilarityShape(shape)) {
    return makeTextSimilarityFilter(shape.props);
  }

  if (isSocialGraphShape(shape)) {
    return makeSocialGraphFilter(shape.props);
  }

  if (isStarterPackShape(shape)) {
    return makeStartPackFilter(shape.props);
  }

  if (isListMemberShape(shape)) {
    return makeListMemberFilter(shape.props);
  }

  if (isSocialListShape(shape)) {
    return makeSocialListFilter(shape.props);
  }

  if (isMagicAudienceShape(shape)) {
    return makeMagicAudienceFilter(shape.props);
  }

  if (isContentModerationShape(shape)) {
    return makeContentModerationFilter(shape.props);
  }

  if (isModelProbabilityShape(shape)) {
    return makeModelProbabilityFilter(shape.props);
  }

  if (isEntityListShape(shape)) {
    return makeEntityListFilter(shape.props);
  }

  if (isLanguageAnalysisShape(shape)) {
    return makeLanguageAnalysisFilter(shape.props);
  }

  if (isSentimentAnalysisShape(shape)) {
    return makeSentimentAnalysisFilter(shape.props);
  }

  if (isFinancialSentimentAnalysisShape(shape)) {
    return makeFinancialSentimentAnalysisFilter(shape.props);
  }

  if (isToxicityAnalysisShape(shape)) {
    return makeToxicityAnalysisFilter(shape.props);
  }

  if (isEmotionAnalysisShape(shape)) {
    return makeEmotionAnalysisFilter(shape.props);
  }

  if (isSpamAnalysisShape(shape)) {
    return makeSpamAnalysisFilter(shape.props);
  }

  if (isTopicAnalysisShape(shape)) {
    return makeTopicAnalysisFilter(shape.props);
  }

  if (isTextCategorizationShape(shape)) {
    return makeTextCategorizationFilter(shape.props);
  }

  if (isImageModerationShape(shape)) {
    return makeImageModerationFilter(shape.props);
  }

  if (isImageCategorizationShape(shape)) {
    return makeImageCategorizationFilter(shape.props);
  }

  if (isPostTypeShape(shape)) {
    return makePostTypeFilter(shape.props);
  }

  if (isEachShape(shape)) {
    const bindings = editor.getBindingsInvolvingShape(shape);
    const boundTo = bindings
      .filter((b) => b.fromId === shape.id)
      .map((b) => editor.getShape(b.toId))
      .filter((s): s is TLShape => Boolean(s))
      .sort(compareShapesByCanvasPosition(editor))
      .map((s) => buildTree(editor, s));

    return {
      each: [shape.props.var, boundTo[0] || { and: [] }],
      metadata: shape.props.metadata,
    };
  }

  if (isCustomNodeShape(shape)) {
    // Expanded here for the canvas and the JSON view only. What gets SAVED is
    // the reference form produced by toReferenceLeaves() below, which the
    // server compiles — so these bytes are a local preview, never the stored
    // truth. Keeping the preview local is what keeps it instant.
    return {
      ...replaceCustomNodeParameters(
        shape.props.filter,
        shape.props.filter.metadata?.customNodeParameters || [],
      ),
      metadata: {
        ...shape.props.node,
        isCustomNode: true,
        customNodeAutoUpdate: shape.props.metadata?.customNodeAutoUpdate,
        color:
          shape.props.metadata?.color || shape.props.filter.metadata?.color,
        component_name: shape.props.node.title,
        customNodeParameters: shape.props.filter.metadata?.customNodeParameters,
        customNodeParameterGroups:
          shape.props.filter.metadata?.customNodeParameterGroups,
      },
    };
  }

  if (isLogicShape(shape)) {
    const bindings = editor.getBindingsFromShape(shape, "layout");
    const boundTo = bindings
      .filter((b) => b.fromId === shape.id)
      .map((b) => editor.getShape(b.toId))
      .filter((s): s is TLShape => Boolean(s))
      .sort(compareShapesByCanvasPosition(editor))
      .map((s) => buildTree(editor, s));

    const filter =
      shape.props.type === "and"
        ? { and: boundTo, metadata: { ...shape.props.metadata } }
        : { or: boundTo, metadata: { ...shape.props.metadata } };

    if (filter.metadata && !filter.metadata.isCustomNode) {
      delete filter.metadata.customNodeAutoUpdate;
      delete filter.metadata.customNodeParameterGroups;
      delete filter.metadata.customNodeParameters;
    }

    return filter;
  }

  if (isOpenAIShape(shape)) {
    return makeOpenAIFilter(shape.props);
  }

  console.log("Unimplemented", shape);
  throw new Error(`The feed builder cannot serialize a "${shape.type}" node.`);
}

export function buildManifestFromState(editor: Editor):
  | {
      manifest: Manifest;
      order: FeedVersion["order"];
      custom_sort_settings: SortingParams | undefined;
    }
  | undefined {
  const feed = getFeedShape(editor) || getCustomNodeRootShape(editor);
  if (!feed) return;

  // The ordering and its layers are read from the LAYER blocks on the page, not
  // from the Feed shape alone — see `layers.ts`. Computed before the match tree
  // so a feed with no rules yet still keeps its layers.
  const sorting = isCustomNodeRootShape(feed)
    ? { order: "new" as const, custom_sort_settings: undefined }
    : buildSortSettings(editor, feed);

  // The rules root is the Feed's first NON-layer child; its layers sit in the
  // Page section and compile through `buildSortSettings` above, never here.
  const binding = editor
    .getBindingsFromShape<LayoutBinding>(feed, "layout")
    .find((b) => {
      const s = editor.getShape(b.toId);
      return s && !isPageRegionChild(s);
    });
  if (!binding) {
    return {
      manifest: { filter: {} as FilterTypes },
      ...sorting,
    };
  }

  const rootShape = editor.getShape(binding.toId);

  if (!rootShape) {
    return {
      manifest: { filter: {} as FilterTypes },
      ...sorting,
    };
  }

  const tree = buildTree(editor, rootShape);

  if (isCustomNodeRootShape(feed)) {
    tree.metadata = {
      ...tree.metadata,
      customNodeParameters: feed.props.customNodeParameters,
      customNodeParameterGroups: feed.props.customNodeParameterGroups,
    };

    return {
      manifest: { filter: tree },
      order: "new",
      custom_sort_settings: undefined,
    };
  } else {
    return {
      ...sorting,
      manifest: { filter: tree },
    };
  }
}

/**
 * Stored metadata is not typed at rest, so coerce anything that is not a real
 * number to 0 rather than passing it through to a validator that requires one.
 */
function asShapeId(value: unknown): number {
  return typeof value === "number" && Number.isFinite(value) ? value : 0;
}

/**
 * Rewrite every custom-node leaf as a reference to (component, version,
 * values), for sending to the server.
 *
 * Applied once, at the wire boundary, rather than inside buildTree: the canvas
 * and the JSON view keep working with the expanded form, so previews stay
 * instant and pasting a manifest back still rebuilds a canvas. What changes is
 * only what we POST — and therefore who decides the stored bytes. The server
 * compiles the reference with the single remaining implementation of
 * substitution, so the client's local expansion can no longer drift into
 * production.
 *
 * Only parameters the user actually SET are sent. That sparseness is
 * load-bearing rather than tidy: substitution reads `value ?? exampleValue`, and
 * a number parameter takes a different branch depending on whether `value` is
 * present at all, so "unset" and "set to the same value as the default" are not
 * interchangeable. Sending resolved values would silently change what some
 * nodes expand to.
 *
 * The server normalizes references back into the shape it has always stored
 * (app/components_v2/reference_leaves.py), so this is safe to send whether or
 * not a given feed has been cut over.
 */
function toReferenceLeaves(filter: FilterTypes): SaveFilter {
  if (!filter || typeof filter !== "object") return filter;

  // A custom node must be recognised BEFORE recursing into and/or/each. Its
  // expanded body IS a logic group -- nearly every component expands to an
  // "and"/"or" tree -- so testing for those first walks straight past the node
  // and the isCustomNode check below is never reached. The failure is silent:
  // the leaf goes over the wire expanded, the server has nothing to normalise,
  // and references (one expander, server-side) buy nothing. Measured against
  // prod: zero reference leaves reached the server while this check came last.
  //
  // Nor should we recurse INTO a custom node: what is inside it is the
  // component's own expansion, not user-authored logic.
  const metadata = filter.metadata;

  if (!metadata?.isCustomNode) {
    if ("and" in filter) {
      return { ...filter, and: filter.and.map(toReferenceLeaves) };
    }

    if ("or" in filter) {
      return { ...filter, or: filter.or.map(toReferenceLeaves) };
    }

    if ("each" in filter) {
      return {
        ...filter,
        each: [filter.each[0], toReferenceLeaves(filter.each[1])],
      };
    }

    return filter;
  }

  const componentId = metadata.component_id;
  // 0 means "no component", not component zero: it is what the shape defaults to
  // and what a node with no component id is rebuilt with. The server agrees --
  // `reference.get("component_id") or ...` treats 0 as missing and answers 400
  // "Unresolvable custom node reference" -- so sending it would turn a node that
  // merely cannot auto-update into a feed that cannot save.
  if (typeof componentId !== "number" || componentId <= 0) {
    // Without a component id there is nothing to reference. Leave the expanded
    // form alone: the server stores it verbatim, exactly as it does today, and
    // the install shows up as detached for its owner to look at.
    return filter;
  }

  const values: Record<string, unknown> = {};
  for (const parameter of metadata.customNodeParameters || []) {
    if (
      parameter?.name &&
      parameter.value !== undefined &&
      parameter.value !== null
    ) {
      values[parameter.name] = parameter.value;
    }
  }

  return {
    $component: {
      component_id: componentId,
      version: metadata.version_id,
      values,
      track_latest: Boolean(metadata.customNodeAutoUpdate),
      // The declared dependency constraint. The server drops anything it does
      // not recognise and stores nothing for `pinned`, so sending it verbatim
      // is safe -- but sending it AT ALL is not optional: reference leaves are
      // a wire format, and a field the editor omits is a field the save can
      // never recover.
      dependency_intent: metadata.dependency_intent,
      display: {
        title: metadata.title,
        description: metadata.description,
        color: metadata.color,
        component_name: metadata.component_name ?? metadata.title,
      },
      customNodeParameterGroups: metadata.customNodeParameterGroups,
    },
  };
}

/** The manifest as it should be POSTed: custom nodes as references. */
export function toSaveManifest(manifest: Manifest): SaveManifest {
  return { ...manifest, filter: toReferenceLeaves(manifest.filter) };
}

function sanitizeCustomParameterInput(value: string) {
  // eslint-disable-next-line no-control-regex
  return value.replace(/[\\"\u0000-\u001F\u2028\u2029]/g, (char) => {
    switch (char) {
      case "\\":
        return "\\\\";
      case '"':
        return '\\"';
      case "\b":
        return "\\b";
      case "\f":
        return "\\f";
      case "\n":
        return "\\n";
      case "\r":
        return "\\r";
      case "\t":
        return "\\t";
      default:
        // For other control characters, use unicode escape sequence
        return "\\u" + ("0000" + char.charCodeAt(0).toString(16)).slice(-4);
    }
  });
}

function expandEachIfPresent(
  filter: FilterTypes,
  parameters: FeedParameter[],
): FilterTypes[] | FilterTypes {
  if (isEachFilter(filter)) {
    const paramName = filter.each[0].replace(/^\$/, "");
    const param = parameters.find((p) => p.name === paramName) as
      | FeedListParameter
      | undefined;

    if (!param) {
      throw new Error(`Parameter ${paramName} not found`);
    }

    const value = param.value ?? param.exampleValue;
    const results =
      value.map((v) =>
        replaceCustomNodeParameters(filter.each[1], [
          ...parameters,
          {
            name: `${paramName}_ITEM`,
            exampleValue: v,
            value: v,
            type: "input",
            description: "",
          },
        ]),
      ) ?? [];

    if (results.length) {
      return results.map((r) => expandEaches(r, parameters));
    }
  }

  return expandEaches(filter, parameters);
}

function expandEaches(
  filter: FilterTypes,
  parameters: FeedParameter[],
): FilterTypes {
  if ("and" in filter) {
    return {
      and: filter.and.map((f) => expandEachIfPresent(f, parameters)).flat(),
      metadata: filter.metadata,
    };
  }

  if ("or" in filter) {
    return {
      or: filter.or.map((f) => expandEachIfPresent(f, parameters)).flat(),
      metadata: filter.metadata,
    };
  }

  return filter;
}

export function replaceCustomNodeParameters(
  filter: FilterTypes,
  parameters: FeedParameter[],
) {
  let stringified = JSON.stringify(expandEaches(filter, parameters), null, 2);

  // Put the longest named params first so that if a short param is a substring
  // of a longer param, it still works.
  const sortedParams = [...parameters].sort((a, b) =>
    b.name.localeCompare(a.name),
  );

  for (const parameter of sortedParams) {
    const regex = new RegExp(`\\$${parameter.name}`, "g");

    if (
      parameter.type === "input" ||
      parameter.type === "regex" ||
      parameter.type === "select" ||
      // if a number is a $PARAM we need to handle that
      (parameter.type === "number" && typeof parameter.value === "string") ||
      !parameter.type
    ) {
      // For simple input parameters, we sanitize the example value and just replace it
      stringified = stringified.replace(
        regex,
        sanitizeCustomParameterInput(parameter.value ?? parameter.exampleValue),
      );
    } else if (parameter.type === "toggle") {
      const toggleRegex = new RegExp(`"\\$${parameter.name}"`, "g");
      stringified = stringified.replace(
        toggleRegex,
        `${parameter.value ?? parameter.exampleValue}`,
      );
    } else if (parameter.type === "list") {
      // Treat each usage as a "pattern" and iterate over all the example values
      // to apply to the pattern.
      const simpleListRegexReplacement = new RegExp(
        `"(.*\\$${parameter.name}.*)"`,
        "g",
      );

      for (const match of stringified.matchAll(simpleListRegexReplacement)) {
        const v = (parameter.value ?? parameter.exampleValue)
          .map(sanitizeCustomParameterInput)
          .map((v) => match[1]?.replace(regex, v) || v)
          .map((v) => `"${v}"`)
          .join(",");

        stringified = stringified.replace(match[0], v);
      }
    } else if (parameter.type === "number") {
      // first replace the example value as an actual number
      const numberRegex = new RegExp(`"\\$${parameter.name}"`, "g");
      stringified = stringified.replace(
        numberRegex,
        `${parameter.value ?? parameter.exampleValue}`,
      );

      // then replace the example value as a percentage
      const percentageRegex = new RegExp(`(".*)\\$${parameter.name}(.*")`, "g");
      stringified = stringified.replace(
        percentageRegex,
        `$1${parameter.value ?? parameter.exampleValue}$2`,
      );
    }
  }

  return JSON.parse(stringified) as FilterTypes;
}

/**
 * The parameter levels a custom node is expanded through, innermost first: its
 * own parameters, then each enclosing custom node's, outward to the definition
 * being edited.
 *
 * Levels, not one merged array -- see expandParameterLevels.
 */
export function parameterLevelsForShape(
  editor: Editor,
  shape: CustomNodeShape,
): FeedParameter[][] {
  const levels: FeedParameter[][] = [
    shape.props.filter.metadata?.customNodeParameters || [],
  ];

  let parentId = shape.parentId as TLShapeId | undefined;

  while (parentId) {
    const parentShape: TLShape | undefined = editor.getShape(parentId);

    if (parentShape) {
      if (isCustomNodeShape(parentShape)) {
        levels.push(
          parentShape.props.filter.metadata?.customNodeParameters || [],
        );
      } else if (isCustomNodeRootShape(parentShape)) {
        levels.push(parentShape.props.customNodeParameters || []);
      }
    }

    parentId = parentShape?.parentId as TLShapeId | undefined;
  }

  return levels;
}

/**
 * Expand a nested custom node one parameter level at a time.
 *
 * Each level is its own substitution pass, because that is how the real path
 * composes: buildTree expands an embedded node with its OWN parameters only,
 * which leaves an outer `$PARAM` threaded through one of that node's values
 * literal -- to be substituted later, by the enclosing node's expansion. The
 * server compiles the same way.
 *
 * Substituting one merged array is NOT the same function, which is why the
 * preview used to disagree with what publishing produced. A single pass
 * substitutes in reverse alphabetical order, so an outer `$FIELD` reached an
 * embedded slot only when that slot's own parameter name sorted after FIELD:
 * a node threading `$FIELD` into both `ROLE_OF` (expanded before FIELD's turn,
 * so substituted) and `AT_ROLE` (expanded after, so left raw) showed one of
 * the two still holding `$FIELD` on screen, while both expanded correctly on
 * save. Reported by a user nesting one custom node inside another.
 */
export function expandParameterLevels(
  filter: FilterTypes,
  levels: FeedParameter[][],
): FilterTypes {
  return levels.reduce<FilterTypes>(
    (expanded, parameters) =>
      // A level that declares no parameters is skipped rather than passed an
      // empty array: `each` throws "Parameter X not found" against a pass that
      // cannot see the parameter it iterates, and an ancestor with nothing of
      // its own should not be able to fail the preview.
      parameters.length
        ? replaceCustomNodeParameters(expanded, parameters)
        : expanded,
    filter,
  );
}

/**
 * The install-owned half of a custom node's metadata, read back off the
 * manifest leaf so a rebuilt shape can carry it on `props.metadata`.
 *
 * A custom node holds its metadata twice, and the two halves mean different
 * things: `props.filter.metadata` belongs to the COMPONENT (its body, its
 * parameters, the author's colour), while `props.metadata` belongs to this
 * INSTALL -- the feed owner's auto-update choice, and any title, colour or
 * pattern they set on the node. `buildTree` reads the auto-update flag from
 * `props.metadata` and nowhere else, and every other path that creates one of
 * these shapes sets it (the install tool from the component metadata,
 * `mergeNodeVersion` by preserving it). Creating the shape without it is what
 * made a rebuild clear the flag: the next save sent `track_latest: false` and
 * the feed stopped tracking -- the same failure as ui PR #76, one loader over.
 *
 * Only known display fields are picked, never spread. `props.filter` is
 * validated as `T.any`, but `props.metadata` is validated by `metadataType`,
 * which rejects unknown properties -- and the parameter arrays are the one part
 * of a leaf that a strict validator could plausibly reject, since they are
 * stored by authors going back to the first components. They are deliberately
 * left out: the authoritative copy lives on `props.filter.metadata`, which is
 * what every reader of parameters already uses, and a second copy here would
 * only go stale as parameters are edited.
 */
/** A leaf's metadata reduced to the properties `metadataType` declares. */
function pickShapeMetadata(
  metadata: NodeMetadata | null | undefined,
): NodeMetadata | null | undefined {
  if (!metadata) return metadata;
  const picked: Record<string, unknown> = {};
  for (const key of metadataKeys) {
    if (key in metadata) picked[key] = (metadata as Record<string, unknown>)[key];
  }
  return picked as NodeMetadata;
}

function installMetadataFromLeaf(metadata: NodeMetadata): NodeMetadata {
  return {
    // `isCustomNode` marks the manifest LEAF, not the shape -- on the canvas a
    // custom node's identity is its shape type. The same reason the caller
    // clears it on the filter copy it stores alongside this one.
    isCustomNode: false,
    customNodeAutoUpdate: metadata.customNodeAutoUpdate,
    color: metadata.color,
    pattern: metadata.pattern,
    component_name: metadata.component_name,
    // `buildTree` writes the component's own name into `title` AND
    // `component_name`, so an equal pair is not a custom title -- the same test
    // `mergeNodeVersion` uses to decide whether a title survives an update.
    // Carrying one anyway would badge the node with its own name twice. A leaf
    // with no `component_name` predates that field and cannot be told apart, so
    // it keeps today's behaviour of no override.
    title:
      metadata.component_name && metadata.title !== metadata.component_name
        ? metadata.title
        : undefined,
  };
}

/**
 * Say which rule the builder could not draw, by its condition keys.
 *
 * `metadata` rides along on every leaf, so it is never the answer to "which rule is
 * this" -- and a filter with nothing else in it is its own distinct failure, worth
 * saying plainly rather than reporting as a rule named "".
 */
function describeUndrawableFilter(filter: FilterTypes): string {
  const keys = Object.keys(filter || {}).filter((key) => key !== "metadata");
  return keys.length
    ? `The feed builder has no rule for ${keys.map((key) => `"${key}"`).join(", ")}.`
    : "The feed builder was handed a rule with nothing in it.";
}

export function buildBindings(options: {
  editor: Editor;
  toId?: TLShapeId;
  filter: FilterTypes;
  index?: IndexKey;
  prevIndex?: IndexKey;
  isReadOnly?: boolean;
  id?: TLShapeId;
}) {
  const {
    editor,
    toId,
    filter,
    index: indexProp,
    prevIndex,
    isReadOnly,
    id,
  } = options;
  const childShapeId = id || createShapeId();
  const parentShape = toId ? editor.getShape<CustomShapes>(toId) : undefined;
  let shouldBuildBindings = true;

  // Every shape below validates its `metadata` prop against `metadataType`,
  // which REJECTS a property it does not declare -- and the value handed to it
  // is a manifest leaf's metadata, a wire format that gains a field whenever
  // the feature behind it ships. One undeclared field is not a dropped field,
  // it is a ValidationError from inside tldraw's store, which replaces the
  // whole canvas with an error card reading "Unexpected property" and nothing
  // about which property or which node.
  //
  // `dependency_intent` did exactly that on 2026-09-04: expanding an embedded
  // custom node's preview builds shapes from the expanded body, whose root
  // metadata IS the leaf's metadata, so the chevron took the canvas down for
  // any node whose author had set an intent.
  //
  // Filtering to the declared keys cannot lose anything: a key the schema does
  // not declare could never have been stored on this prop in the first place --
  // it would have thrown. What a leaf carries beyond them survives on the
  // custom node's `props.filter.metadata` (validated `T.any`) and on
  // `props.node`, which is what `buildTree` reads back out.
  const metadata = pickShapeMetadata(filter.metadata);

  const padding =
    parentShape && "padding" in parentShape.props
      ? parentShape.props.padding
      : { top: 0, right: 0, bottom: 0, left: 0, gap: 0 };
  const x = (parentShape ? parentShape.x : 0) + padding.left * 2;
  const y = (parentShape ? parentShape.y : 0) + padding.top - 20;

  if (filter.metadata?.isCustomNode) {
    shouldBuildBindings = false;
    editor.createShape({
      type: customNodeType,
      x,
      y,
      id: childShapeId,
      props: {
        isReadOnly,
        filter: {
          ...filter,
          metadata: {
            ...filter.metadata,
            isCustomNode: false,
          },
        },
        metadata: installMetadataFromLeaf(filter.metadata),
        node: {
          title: filter.metadata.title,
          description: filter.metadata.description,
          // A stored node can carry `isCustomNode` and no component id at all --
          // 8 live components are in that state, and `toReferenceLeaves` already
          // tolerates the same thing on the way out. The shape declares both ids
          // as REQUIRED numbers, so passing undefined made tldraw refuse the
          // record with "At component_id: Expected number, got undefined" and
          // took the whole page down: every canvas built from the filter rather
          // than from a saved snapshot, which is exactly the custom-node view and
          // edit pages.
          //
          // 0 is this codebase's existing "no component" sentinel --
          // `getDefaultProps` uses it, and `custom-node.tsx` skips the component
          // fetch while it is falsy, so the node renders as a detached one.
          component_id: asShapeId(filter.metadata.component_id),
          version_id: asShapeId(filter.metadata.version_id),
          // Read back in, because `buildTree` emits the leaf's metadata from
          // `props.node` and `toReferenceLeaves` sends `dependency_intent` from
          // there. Leaving it off meant every canvas rebuilt from a manifest --
          // a remix, a keystroke in the JSON editor, any feed loaded from its
          // filter rather than a snapshot -- dropped the author's declared
          // intent, and the next save wrote the embed back to `pinned`.
          dependency_intent: filter.metadata.dependency_intent,
        },
      },
    });
  } else if ("and" in filter || "or" in filter) {
    const isAnd = "and" in filter;
    editor.createShape({
      type: logicType,
      x,
      y,
      id: childShapeId,
      props: {
        isReadOnly,
        type: isAnd ? "and" : "or",
        metadata,
      },
    });
  } else if ("each" in filter) {
    editor.createShape({
      type: eachType,
      x,
      y,
      id: childShapeId,
      props: {
        isReadOnly,
        metadata,
        var: filter.each[0],
      },
    });
  } else if (isRegexFilter(filter)) {
    editor.createShape({
      type: simpleRegexType,
      x,
      y,
      id: childShapeId,
      props: {
        isReadOnly,
        type: "match",
        var: filter.regex_matches[0],
        regex: filter.regex_matches[1],
        insensitive: filter.regex_matches[2] ?? false,
        metadata,
      },
    });
  } else if (isRegexAnyFilter(filter)) {
    editor.createShape({
      type: multiRegexType,
      x,
      y,
      id: childShapeId,
      props: {
        isReadOnly,
        type: "any",
        var: filter.regex_any[0],
        regex: filter.regex_any[1],
        insensitive: filter.regex_any[2] ?? false,
        regex_match: filter.regex_any[3] ?? false,
        separator: filter.regex_any[4] ?? undefined,
        metadata,
      },
    });
  } else if (isRegexNoneFilter(filter)) {
    editor.createShape({
      type: multiRegexType,
      x,
      y,
      id: childShapeId,
      props: {
        isReadOnly,
        type: "none",
        var: filter.regex_none[0],
        regex: filter.regex_none[1],
        insensitive: filter.regex_none[2] ?? false,
        regex_match: filter.regex_none[3] ?? false,
        separator: filter.regex_none[4] ?? undefined,
        metadata,
      },
    });
  } else if (isRegexNegationFilter(filter)) {
    editor.createShape({
      type: simpleRegexType,
      x,
      y,
      id: childShapeId,
      props: {
        isReadOnly,
        type: "exclude",
        var: filter.regex_negation_matches[0],
        regex: filter.regex_negation_matches[1],
        insensitive: filter.regex_negation_matches[2] ?? false,
        metadata,
      },
    });
  } else if (isTextSimilarityFilter(filter)) {
    editor.createShape({
      type: textSimilarityType,
      x,
      y,
      id: childShapeId,
      props: {
        isReadOnly,
        var: filter.text_similarity[0],
        anchorText: filter.text_similarity[1].anchor_text,
        modelName: filter.text_similarity[1].model_name,
        operator: filter.text_similarity[2],
        threshold: filter.text_similarity[3],
        metadata,
      },
    });
  } else if (isAttributeCompareFilter(filter)) {
    editor.createShape({
      type: attributeCompareType,
      x,
      y,
      id: childShapeId,
      props: {
        isReadOnly,
        var: filter.attribute_compare[0],
        operator: filter.attribute_compare[1],
        value: filter.attribute_compare[2],
        metadata,
      },
    });
  } else if (isHourOfDayBetweenFilter(filter)) {
    editor.createShape({
      type: hourOfDayBetweenType,
      x,
      y,
      id: childShapeId,
      props: {
        isReadOnly,
        var: filter.hour_of_day_between[0],
        // Stored as numbers in the manifest and as strings on the shape, because
        // every select on the canvas works in strings. Round-tripping through
        // `String()` (inside `readTimeWindows`) is what keeps a reopened feed
        // identical to the saved one. Slots 1 and 2 are a scalar each for one
        // window, or two parallel arrays for many.
        windows: readTimeWindows(
          filter.hour_of_day_between[1],
          filter.hour_of_day_between[2],
        ),
        utcOffsetMinutes: String(filter.hour_of_day_between[3] ?? 0),
        metadata,
      },
    });
  } else if (isDateBetweenFilter(filter)) {
    editor.createShape({
      type: dateBetweenType,
      x,
      y,
      id: childShapeId,
      props: {
        isReadOnly,
        var: filter.date_between[0],
        // Bounds are kept VERBATIM: a bare date, a naive datetime and a zoned one
        // mean different things to the engines, and the node shows each as it is.
        windows: readTimeWindows(filter.date_between[1], filter.date_between[2]),
        utcOffsetMinutes: String(filter.date_between[3] ?? 0),
        metadata,
      },
    });
  } else if (isRatioCompareFilter(filter)) {
    editor.createShape({
      type: ratioCompareType,
      x,
      y,
      id: childShapeId,
      props: {
        isReadOnly,
        numerator: filter.ratio_compare[0],
        denominator: filter.ratio_compare[1],
        operator: filter.ratio_compare[2],
        value: filter.ratio_compare[3],
        metadata,
      },
    });
  } else if (isTextQuestionFilter(filter)) {
    editor.createShape({
      type: textQuestionType,
      x,
      y,
      id: childShapeId,
      props: {
        isReadOnly,
        ...textQuestionProps(filter),
        metadata,
      },
    });
  } else if (isEmbedFilter(filter)) {
    editor.createShape({
      type: embedType,
      x,
      y,
      id: childShapeId,
      props: {
        isReadOnly,
        operator: filter.embed_type[0],
        embed_name: filter.embed_type[1],
        metadata,
      },
    });
  } else if (isSocialGraphFilter(filter)) {
    editor.createShape({
      type: socialGraphType,
      x,
      y,
      id: childShapeId,
      props: {
        isReadOnly,
        username: filter.social_graph[0],
        operator: filter.social_graph[1],
        direction: filter.social_graph[2],
        metadata,
      },
    });
  } else if (isStarterPackFilter(filter)) {
    editor.createShape({
      type: starterPackType,
      x,
      y,
      id: childShapeId,
      props: {
        isReadOnly,
        starterPackUrl: filter.starter_pack_member[0],
        operator: filter.starter_pack_member[1],
        metadata,
      },
    });
  } else if (isListMemberFilter(filter)) {
    editor.createShape({
      type: listMemberType,
      x,
      y,
      id: childShapeId,
      props: {
        isReadOnly,
        listUrl: filter.list_member[0],
        operator: filter.list_member[1],
        metadata,
      },
    });
  } else if (isSocialListFilter(filter)) {
    editor.createShape({
      type: socialListType,
      x,
      y,
      id: childShapeId,
      props: {
        isReadOnly,
        dids: filter.social_list[0],
        operator: filter.social_list[1],
        metadata,
      },
    });
  } else if (isMagicAudienceFilter(filter)) {
    editor.createShape({
      type: magicAudienceType,
      x,
      y,
      id: childShapeId,
      props: {
        isReadOnly,
        magicAudienceId: parseInt(filter.magic_audience[0]),
        operator: filter.magic_audience[1],
        metadata,
      },
    });
  } else if (isModelProbabilityFilter(filter)) {
    editor.createShape({
      type: modelProbabilityType,
      x,
      y,
      id: childShapeId,
      props: {
        isReadOnly,
        modelName: filter.model_probability[0],
        operator: filter.model_probability[1],
        threshold: filter.model_probability[2],
        metadata,
      },
    });
  } else if (isContentModerationFilter(filter)) {
    editor.createShape({
      type: contentModerationType,
      x,
      y,
      id: childShapeId,
      props: {
        isReadOnly,
        moderationCategory: filter.content_moderation[0],
        operator: filter.content_moderation[1],
        value: filter.content_moderation[2],
        metadata,
      },
    });
  } else if (isEntityListFilter(filter)) {
    if ("entity_matches" in filter) {
      editor.createShape({
        type: entityListType,
        x,
        y,
        id: childShapeId,
        props: {
          isReadOnly,
          operator: "in",
          type: filter.entity_matches[0],
          entities: filter.entity_matches[1],
          metadata,
        },
      });
    } else {
      editor.createShape({
        type: entityListType,
        x,
        y,
        id: childShapeId,
        props: {
          isReadOnly,
          operator: "not_in",
          type: filter.entity_excludes[0],
          entities: filter.entity_excludes[1],
          metadata,
        },
      });
    }
  } else if (isLanguageAnalysisFilter(filter)) {
    editor.createShape({
      type: languageAnalysisType,
      x,
      y,
      id: childShapeId,
      props: {
        isReadOnly,
        languageAnalysisCategory: filter.language_analysis[0],
        operator: filter.language_analysis[1],
        value: filter.language_analysis[2],
        metadata,
      },
    });
  } else if (isSentimentAnalysisFilter(filter)) {
    editor.createShape({
      type: sentimentAnalysisType,
      x,
      y,
      id: childShapeId,
      props: {
        isReadOnly,
        sentimentAnalysisCategory: filter.sentiment_analysis[0],
        operator: filter.sentiment_analysis[1],
        value: filter.sentiment_analysis[2],
        metadata,
      },
    });
  } else if (isFinancialSentimentAnalysisFilter(filter)) {
    editor.createShape({
      type: financialSentimentAnalysisType,
      x,
      y,
      id: childShapeId,
      props: {
        isReadOnly,
        financialSentimentAnalysisCategory:
          filter.financial_sentiment_analysis[0],
        operator: filter.financial_sentiment_analysis[1],
        value: filter.financial_sentiment_analysis[2],
        metadata,
      },
    });
  } else if (isToxicityAnalysisFilter(filter)) {
    editor.createShape({
      type: toxicityAnalysisType,
      x,
      y,
      id: childShapeId,
      props: {
        isReadOnly,
        toxicityAnalysisCategory: filter.toxicity_analysis[0],
        operator: filter.toxicity_analysis[1],
        value: filter.toxicity_analysis[2],
        metadata,
      },
    });
  } else if (isEmotionAnalysisFilter(filter)) {
    editor.createShape({
      type: emotionAnalysisType,
      x,
      y,
      id: childShapeId,
      props: {
        isReadOnly,
        emotionAnalysisCategory: filter.emotion_sentiment_analysis[0],
        operator: filter.emotion_sentiment_analysis[1],
        value: filter.emotion_sentiment_analysis[2],
        metadata,
      },
    });
  } else if (isSpamAnalysisFilter(filter)) {
    editor.createShape({
      type: spamAnalysisType,
      x,
      y,
      id: childShapeId,
      props: {
        isReadOnly,
        topicAnalysisCategory: filter.marketing_check[0],
        operator: filter.marketing_check[1],
        value: filter.marketing_check[2],
        metadata,
      },
    });
  } else if (isTopicAnalysisFilter(filter)) {
    editor.createShape({
      type: topicAnalysisType,
      x,
      y,
      id: childShapeId,
      props: {
        isReadOnly,
        topicAnalysisCategory: filter.topic_analysis[0],
        operator: filter.topic_analysis[1],
        value: filter.topic_analysis[2],
        metadata,
      },
    });
  } else if (isTextCategorizationFilter(filter)) {
    editor.createShape({
      type: textCategorizationType,
      x: 300,
      y: 300,
      id: childShapeId,
      props: {
        isReadOnly,
        tag: filter.text_arbitrary[0],
        operator: filter.text_arbitrary[1],
        value: filter.text_arbitrary[2],
        metadata,
      },
    });
  } else if (isImageModerationFilter(filter)) {
    editor.createShape({
      type: imageModerationType,
      x: 300,
      y: 300,
      id: childShapeId,
      props: {
        isReadOnly,
        imageModerationCategory: filter.image_nsfw[0],
        operator: filter.image_nsfw[1],
        value: filter.image_nsfw[2],
        metadata,
      },
    });
  } else if (isImageCategorizationFilter(filter)) {
    editor.createShape({
      type: imageCategorizationType,
      x: 300,
      y: 300,
      id: childShapeId,
      props: {
        isReadOnly,
        tag: filter.image_arbitrary[0],
        operator: filter.image_arbitrary[1],
        value: filter.image_arbitrary[2],
        metadata,
      },
    });
  } else if (isPostTypeFilter(filter)) {
    editor.createShape({
      type: postTypeType,
      x,
      y,
      id: childShapeId,
      props: {
        isReadOnly,
        operator: filter.post_type[0],
        post_type: filter.post_type[1][0],
        metadata,
      },
    });
  } else if (isParamCompareFilter(filter)) {
    editor.createShape({
      type: paramCompareType,
      x,
      y,
      id: childShapeId,
      props: {
        isReadOnly,
        var: `${filter.param_compare[0]}`,
        operator: filter.param_compare[1],
        compare: `${filter.param_compare[2]}`,
        metadata,
      },
    });
  } else if (isOpenAIFilter(filter)) {
    // Via openAIFilterArgs, because this filter arrives under either `text_predicate` or the
    // legacy `openai_prompt` and the shape is the same either way.
    const [prompt, operator, threshold, overQuota] = openAIFilterArgs(filter);
    editor.createShape({
      type: openaiType,
      x,
      y,
      id: childShapeId,
      props: {
        isReadOnly,
        prompt,
        operator,
        threshold,
        overQuota,
        metadata,
      },
    });
  } else {
    console.log("Unimplemented", filter);
    // Naming the condition matters more than it looks: this throw reaches the user as
    // the whole message of an error banner, on the generate path as well as on load, and
    // a bare "Unimplemented" tells neither them nor us WHICH rule the builder has no node
    // for. A generated design that reached for `no_nsfw` took a beta author's generate
    // down on 2026-09-04 and the only place the key appeared was the browser console.
    throw new Error(describeUndrawableFilter(filter));
  }

  const index = indexProp || getIndexAbove(prevIndex);

  if (toId) {
    editor.createBinding({
      fromId: toId,
      toId: childShapeId,
      type: "layout",
      props: { index, placeholder: false },
    });

    const childShape = editor.getShape(childShapeId)!;
    editor.updateShape({
      id: childShapeId,
      type: childShape.type,
      parentId: toId,
    });
  }

  if (
    shouldBuildBindings &&
    ("and" in filter || "or" in filter || "each" in filter)
  ) {
    const children =
      "and" in filter
        ? filter.and
        : "or" in filter
        ? filter.or
        : [filter.each[1]];
    let prevIndexForChild: IndexKey | undefined;

    for (const childFilter of children) {
      prevIndexForChild = buildBindings({
        editor,
        toId: childShapeId,
        filter: childFilter,
        prevIndex: prevIndexForChild,
        isReadOnly,
      }).index;
    }
  }

  return { index, id: childShapeId };
}

/**
 * The first argument that is a non-empty array, or undefined.
 *
 * `a || b` is wrong for arrays here: `[]` is truthy, so an empty list wins over
 * a populated one and the populated one is lost.
 */
function firstNonEmpty<T>(...candidates: (T[] | undefined)[]): T[] | undefined {
  return candidates.find((candidate) => candidate && candidate.length > 0);
}

export function buildInitialEditor(
  editor: Editor,
  manifest?: FeedVersion,
  feedNode?: CanvasEditorProps["rootNode"],
) {
  if (feedNode?.variant === "custom-node") {
    const currentCustomNode = editor.getShape<CustomNodeRootShape>(feedId);
    // A custom node's parameters live on the ROOT SHAPE's props, and
    // buildManifestFromState writes them back over the tree's metadata on the
    // way out. So whatever the manifest being loaded says about them has to be
    // read IN here, or it is silently discarded on the next serialize.
    //
    // That is what made pasting a custom node's JSON lose its parameters:
    // rebuildEditorFromManifest deletes the root before calling this, so
    // `currentCustomNode` is undefined, and the old order fell through to
    // `feedNode` -- the SAVED component -- which for a new node has none. The
    // pasted `customNodeParameters` were never looked at. Reported by a user
    // pasting a node with a SITES parameter and getting `[]` back.
    //
    // firstNonEmpty rather than `||`: an empty array is TRUTHY in JS, so `||`
    // let an empty list win over a populated one -- which is the same shape of
    // bug the two lines below already disagreed about.
    const loadedMetadata = manifest?.manifest?.filter?.metadata;
    editor.createShape({
      type: customNodeRootType,
      x: 0,
      y: 0,
      id: feedId,
      props: {
        ...currentCustomNode?.props,
        customNodeParameterGroups: firstNonEmpty(
          loadedMetadata?.customNodeParameterGroups,
          feedNode?.customNodeParameterGroups,
          currentCustomNode?.props.customNodeParameterGroups,
        ),
        customNodeParameters:
          firstNonEmpty(
            loadedMetadata?.customNodeParameters,
            currentCustomNode?.props.customNodeParameters,
            feedNode?.customNodeParameters,
          ) ?? [],
        title: currentCustomNode?.props.title || feedNode?.title,
        description:
          currentCustomNode?.props.description || feedNode?.description,
      },
    });
  } else {
    const currentFeed = editor.getShape<FeedShape>(feedId);
    editor.createShape({
      type: feedType,
      x: 0,
      y: 0,
      id: feedId,
      props: {
        ...currentFeed?.props,
        order: currentFeed?.props.order || manifest?.order || "new",
        title: currentFeed?.props.title || feedNode?.title,
        description: currentFeed?.props.description || feedNode?.description,
        // The manifest is the third fallback, and without it the audience
        // setting is silently dropped on the way in. Both other sources are
        // absent on the path that matters: `rebuildEditorFromManifest` deletes
        // `feedId` before calling here, so `currentFeed` is undefined, and the
        // feed edit route passes no `rootNode` (only the custom-node routes
        // do). So one keystroke in the JSON editor — `onJsonEditorChange` runs
        // on every successful parse — rebuilt the feed shape with no lens, and
        // the next serialize wrote that back as the truth.
        //
        // Exactly the bug fixed for custom-node parameters a few lines above,
        // in the same function, for the same reason: whatever the manifest
        // being loaded says has to be read IN here, or it is discarded on the
        // next serialize. `order` on the line above already does this.
        custom_sort_settings:
          currentFeed?.props.custom_sort_settings ||
          feedNode?.custom_sort_settings ||
          manifest?.custom_sort_settings,
      },
    });

    // The layers beside the feed are built from the same settings, here, so a
    // canvas built from a manifest shows its layers as it shows its rules.
    const feed = editor.getShape<FeedShape>(feedId);
    if (feed) buildLayerShapes(editor, feed);
  }

  if (
    manifest?.manifest?.filter &&
    Object.keys(manifest.manifest.filter).length
  ) {
    buildBindings({ editor, toId: feedId, filter: manifest.manifest.filter });
  }

  return feedId;
}

/**
 * Replace the canvas with the one a manifest describes, atomically.
 *
 * `buildInitialEditor` walks the manifest shape by shape, so a manifest it
 * cannot draw fails part-way through: the root has already been deleted, some
 * rules exist, and the rule it choked on does not. It is not a rare state
 * either -- every keystroke in the JSON editor rebuilds from text that parses
 * long before it describes a whole feed, so deleting a line out of
 * `"regex_matches": ["commit.record.text", "los angeles", true]` leaves a
 * boolean where a rule wants its pattern.
 *
 * Two things have to happen for that to be survivable. The rebuild runs inside
 * a batch of ours and fails inside it, because tldraw treats an error escaping
 * its OWN outermost batch as a crash it never comes back from: it re-renders
 * the editor as a thrown error, which is what took the builder (and any unsaved
 * JSON next to it) down to the error boundary. And the canvas the failed build
 * was replacing goes back, since catching the error commits the half-built
 * store it left behind.
 *
 * The error is rethrown once the canvas is whole again, so the caller decides
 * what a failed rebuild means for it -- the JSON editor reports it like any
 * other validation error and keeps the user's text.
 */
export function rebuildEditorFromManifest(
  editor: Editor,
  manifest?: FeedVersion,
  feedNode?: CanvasEditorProps["rootNode"],
) {
  const previousCanvas = editor.getSnapshot();
  let failure: Error | undefined;

  editor.run(() => {
    try {
      // Layers are not bound to the feed, so deleting `feedId` does not take
      // them with it the way it takes the match tree.
      deleteLayerShapes(editor);
      editor.deleteShape(feedId);

      // 🔴 IN THE LIVE EDITOR THE FEED SHAPE SURVIVES THAT DELETE. A side effect
      // in `CanvasEditor` refuses to delete the root so a creator cannot erase
      // it — so `buildInitialEditor` finds a `currentFeed` and, by its own
      // rules, keeps that shape's ordering and sort settings over the
      // manifest's. The match tree was still replaced (the children were
      // deletable), which is why this went unnoticed: the rules followed the
      // JSON while the sorting silently did not, and every layer redrawn from
      // the stale shape was the OLD list. Write the manifest onto the survivor
      // first, so the rebuilt feed describes the manifest it was rebuilt from.
      const survivor = editor.getShape<FeedShape>(feedId);
      if (survivor && manifest && isFeedShape(survivor)) {
        editor.updateShape<FeedShape>({
          id: survivor.id,
          type: survivor.type,
          props: {
            order: manifest.order || survivor.props.order,
            custom_sort_settings: manifest.custom_sort_settings ?? undefined,
          },
        });
      }

      buildInitialEditor(editor, manifest, feedNode);
    } catch (error) {
      editor.loadSnapshot(previousCanvas);
      failure = error as Error;
    }
  });

  if (failure) {
    throw failure;
  }

  return feedId;
}
